import React from 'react';
import { sortBy } from 'lodash';
import { withFormsy } from 'formsy-react';
import { useDispatch } from 'react-redux';
import { Box, Chip, CircularProgress, ListItemText, styled, type CreateFilterOptionsConfig } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useDeepCompareEffect } from '@fuse/hooks';

import BaseTextField, { type FieldProps } from './components/BaseTextField';
import { type FiltersProps } from '../hooks/useFilters';
import useSettings from '../hooks/useSettings';
import useCancelablePromise from '../hooks/useCancelablePromise';
import { executeStored } from '../Utils';

export type SearchSelectFieldProps = {
	/** The system table name */
	systable: string;
	/** Function used to determine the string value for a given option */
	getOptionLabel?: (option?: any) => string;
	/** If true, the component allows to set multiple values */
	multiple?: boolean;
	/** Function that determines the options that are eligible */
	filterOptions?: CreateFilterOptionsConfig<any>;
	/** Function that group items by the specified criterium */
	groupBy?: (option?: any) => string;
	/** A list of objects that contain the filters applied to the list request */
	filters?: FiltersProps['filters'];
	/** The query applied to the list request */
	query?: any;
} & Omit<FieldProps<string | string[] | number | number[], any>, 'startAdornment' | 'endAdornment'>;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root > .MuiInputAdornment-root': {
		'&.MuiInputAdornment-positionEnd:has(.SearchSelectField-loading)': {
			position: 'absolute',
			right: 9,
			top: '50%',
			transform: 'translate(0, -50%)'
		}
	}
}));

/** A component that can show a list of items and allows the user to find */
function SearchSelectField({ value, ...props }: SearchSelectFieldProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.systable);
	const getCancelablePromise = useCancelablePromise();

	const idName = `${settings.modu_short}_id`;
	const hasLogs = props.logProps?.hasLogs(props.name);

	const [options, setOptions] = React.useState<any[]>();
	const [isLoading, setIsLoading] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const selectedOption =
		props.multiple && typeof value !== 'string' && typeof value !== 'number'
			? options?.filter(opt => value?.find(v => v === opt[idName])) || []
			: options?.find(opt => opt[idName] === value) || null;

	const isOptionEqualToValue = (option, fieldValue) => option[idName] === fieldValue[idName];

	const getTitle = option => option[`${settings.modu_short}_title`];
	const parseOptionLabel = props.getOptionLabel || getTitle;

	function handleChange(_event, newSelectedValue) {
		if (props.beforeChange) props.beforeChange(newSelectedValue);
		if (newSelectedValue) {
			const newValue = props.multiple ? newSelectedValue.map(v => v[idName]) : newSelectedValue[idName];
			props.setValue(newValue);
			props.sendChange(props.name, newValue);
		} else {
			props.setValue(props.multiple ? [] : null);
			props.sendChange(props.name, props.multiple ? [] : null);
		}
		if (props.afterChange) props.afterChange(newSelectedValue);
	}

	function handleKeyDown(event) {
		if (event.key === 'Enter') {
			event.preventDefault();
		}
	}

	function renderOption({ key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: any) {
		return (
			<Box key={key} component="li" {...optionProps}>
				<ListItemText
					primary={parseOptionLabel(option)}
					secondary={option[`${settings.modu_short}_description`]}
				/>
			</Box>
		);
	}

	function renderValue(fieldValue: any | any[], getItemProps) {
		return fieldValue.map((option, index) => {
			const { key, ...itemProps } = getItemProps({ index });
			return (
				<Chip
					{...itemProps}
					key={key}
					label={parseOptionLabel(option)}
					size="small"
					disabled={props.isEdit && itemProps.disabled}
					onDelete={props.isEdit ? itemProps.onDelete : undefined}
				/>
			);
		});
	}

	useDeepCompareEffect(() => {
		if (!options || open) {
			setIsLoading(true);
			executeStored({
				action: 'st_sys_systable_list',
				params: {
					systable: props.systable,
					filters: props.filters || [],
					query: props.query
				},
				dispatch,
				getCancelablePromise,
				successHandler: response => {
					setOptions(response.data.list || []);
					setIsLoading(false);
				}
			});
		}
	}, [dispatch, props.filters, props.query, props.systable]);

	// Rimuove il valore selezionato se si annulla la modifica
	// React.useEffect(() => {
	// 	if (!props.isEdit && !props.isPristine) {
	// 		props.setValue(selectedOption);
	// 	}
	// }, [props.isEdit]);

	const loadingEndAdornment = (
		<CircularProgress className="SearchSelectField-loading" color="inherit" size={20} disableShrink />
	);

	const groupedOptions = props.groupBy ? sortBy(options, props.groupBy) : options;
	const sortedOptions = props.getOptionLabel ? sortBy(groupedOptions, props.getOptionLabel) : groupedOptions;

	return (
		<StyledAutocomplete
			className={props.className}
			renderInput={params => (
				<BaseTextField
					{...params}
					name={props.name}
					label={props.label}
					isEdit={props.isEdit}
					endAdornment={props.isEdit && isLoading ? loadingEndAdornment : params.InputProps.endAdornment}
					required={props.required}
					errorMessage={props.errorMessage}
					isFormSubmitted={props.isFormSubmitted}
					isPristine={props.isPristine}
					isValid={props.isValid}
					logProps={props.logProps}
				/>
			)}
			options={sortedOptions || []}
			value={selectedOption}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			disabled={!props.isEdit || hasLogs}
			open={props.isEdit && open}
			onOpen={() => !props.logProps?.showLogs && props.isEdit && !isLoading && setOpen(true)}
			onClose={() => setOpen(false)}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			getOptionLabel={parseOptionLabel}
			getOptionDisabled={(option: any) => !!option[`${settings.modu_short}_disabled`]}
			renderOption={renderOption}
			renderValue={props.multiple ? renderValue : undefined}
			isOptionEqualToValue={isOptionEqualToValue}
			filterOptions={createFilterOptions(props.filterOptions)}
			groupBy={props.groupBy}
			multiple={props.multiple}
			disableCloseOnSelect={props.multiple}
			// autoHighlight
			selectOnFocus={false}
			forcePopupIcon={!!props.isEdit && !isLoading}
			disableClearable={!props.isEdit || !!props.required || isLoading}
		/>
	);
}

export default withFormsy(SearchSelectField);
